import { ColumnChart, useChartFormatters } from "@wayflyer/flyui-dataviz";
import { Text } from "@wayflyer/flyui";
import { useIntl } from "react-intl";

import type {
  ProfitabilityChartProps,
  ProfitabilityDataPoint,
} from "../../components/profitability-chart/profitability";
import { periodMovement, profitabilityMeasures, profitabilityValues } from "./profit-chart-data";

export const OverviewProfitChart = (props: ProfitabilityChartProps & { error?: string; currencyCode?: string }) => {
  const intl = useIntl();
  const chartFormat = useChartFormatters({ currency: props.currencyCode ?? "USD" });
  const { data, formatValue, loading, error, thisYearEnabled, lastYearEnabled, resolutionAbreviation } = props;
  const bothPeriods = thisYearEnabled && lastYearEnabled;
  const measures = profitabilityMeasures.filter((measure) => {
    const enabled = {
      Profit: props.profitEnabled,
      Sales: props.salesEnabled,
      SalesIncReturns: props.salesIncReturnsEnabled,
      Marketing: props.marketingEnabled,
      ROAS: props.roasEnabled,
      AOV: props.aovEnabled,
      CAC: props.cacEnabled,
    };
    return (
      enabled[measure] &&
      (measure !== "Profit" ||
        data.some((row) => row.thisYearsProfitValue !== undefined || row.lastYearsProfitValue !== undefined))
    );
  });
  const labels = {
    Profit: intl.formatMessage({ id: "Overview chart: Net Profit", defaultMessage: "Net Profit" }),
    Sales: intl.formatMessage({ id: "Overview chart: Total Sales", defaultMessage: "Total Sales" }),
    SalesIncReturns: intl.formatMessage({ id: "Overview chart: Gross Revenue", defaultMessage: "Gross Revenue" }),
    Marketing: intl.formatMessage({ id: "Overview chart: Marketing", defaultMessage: "Marketing" }),
    ROAS: intl.formatMessage({
      id: "Overview chart: Marketing Efficiency Ratio",
      defaultMessage: "Marketing Efficiency Ratio",
    }),
    AOV: intl.formatMessage({ id: "Overview chart: Average Order Value", defaultMessage: "Average Order Value" }),
    CAC: intl.formatMessage({
      id: "Overview chart: Customer Acquisition Cost",
      defaultMessage: "Customer Acquisition Cost",
    }),
  };
  const colors = { Profit: 6, Sales: 1, SalesIncReturns: 2, Marketing: 3, ROAS: 7, AOV: 4, CAC: 5 } as const;
  const periods = [
    {
      key: "thisYears" as const,
      enabled: thisYearEnabled,
      label: intl.formatMessage({ id: "chart filter - This year", defaultMessage: "This year" }),
    },
    {
      key: "lastYears" as const,
      enabled: lastYearEnabled,
      label: intl.formatMessage({ id: "chart filter - Last year", defaultMessage: "Last year" }),
    },
  ];
  const series = measures.flatMap((measure) =>
    periods
      .filter((period) => period.enabled)
      .map((period) => ({
        key: `${period.key}${measure}`,
        measure,
        label: bothPeriods ? `${labels[measure]} (${period.label})` : labels[measure],
        reference: period.key === "lastYears",
        color: `var(--palette-dataviz-categorical-category${colors[measure]})` as const,
        data: profitabilityValues(data, measure, period.key),
        axis: measure === "ROAS" ? "ratio" : "money",
      }))
  );
  const hasValues = series.some((item) => item.data.some((value) => value.y !== null));
  const status = error ? "unavailable" : loading ? "loading" : hasValues ? "ready" : "empty";
  const money = (value: number) => String(formatValue(value));
  const ratio = (value: number) => String(formatValue(value, true));
  const categoryLabels = new Map(data.map((row) => [row.dayRaw ?? row.day, row.day]));
  const categoryLabel = (key: string) => categoryLabels.get(key) ?? key;
  return (
    <>
      <ColumnChart.Root
        label={intl.formatMessage({ id: "Overview: profitability chart", defaultMessage: "Profitability" })}
        categories={data.map((row) => row.dayRaw ?? row.day)}
        status={status}
        yAxes={[
          {
            id: "money",
            label: props.currencyCode,
            formatValue: money,
            formatTick: props.currencyCode ? chartFormat.compactCurrency : money,
            width: 72,
          },
          ...(props.roasEnabled
            ? [{ id: "ratio", label: "MER", formatValue: ratio, formatTick: ratio, width: 48 }]
            : []),
        ]}
      >
        <ColumnChart.Plot formatCategory={categoryLabel} minHeight={357}>
          {props.profitEnabled ? <ColumnChart.ReferenceLine value={0} yAxis="money" /> : null}
          {series.map((item) =>
            item.measure === "Profit" ? (
              <ColumnChart.Series
                key={item.key}
                seriesKey={item.key}
                label={item.label}
                data={item.data}
                color={item.color}
                yAxis={item.axis}
                fill={item.reference ? "reference" : "solid"}
              />
            ) : (
              <ColumnChart.Line
                key={item.key}
                seriesKey={item.key}
                label={item.label}
                data={item.data}
                color={item.color}
                yAxis={item.axis}
                curve="monotone"
                lineStyle={item.reference ? "dashed" : "solid"}
              />
            )
          )}
          <ColumnChart.Inspector>
            {(point) => (
              <ColumnChart.Tooltip title={categoryLabel(point.category)}>
                {point.entries.map((entry) => {
                  const item = series.find((series) => series.key === entry.seriesKey);
                  const row = entry.point.datum as ProfitabilityDataPoint | undefined;
                  const movement =
                    item && !item.reference && row ? periodMovement(row, item.measure, entry.value) : null;
                  const value = entry.yAxis === "ratio" ? ratio(entry.value) : money(entry.value);
                  return (
                    <ColumnChart.TooltipRow
                      key={entry.seriesKey}
                      label={entry.label}
                      value={movement === null ? value : `${value} (${movement.toFixed(1)}% ${resolutionAbreviation})`}
                    />
                  );
                })}
              </ColumnChart.Tooltip>
            )}
          </ColumnChart.Inspector>
        </ColumnChart.Plot>
        <ColumnChart.Legend>
          {series.map((item) => (
            <ColumnChart.LegendItem
              key={item.key}
              color={item.color}
              fill={item.measure === "Profit" ? (item.reference ? "reference" : "solid") : "line"}
              lineStyle={item.reference ? "dashed" : "solid"}
            >
              {item.label}
            </ColumnChart.LegendItem>
          ))}
        </ColumnChart.Legend>
      </ColumnChart.Root>
      {status === "empty" ? (
        <Text size="sm" color="subtle">
          {data.length && !measures.length
            ? intl.formatMessage({
                id: "Overview: select chart metric",
                defaultMessage: "Select a metric to view the chart",
              })
            : data.length && !thisYearEnabled && !lastYearEnabled
              ? intl.formatMessage({
                  id: "Overview: select chart period",
                  defaultMessage: "Choose a comparison period to view the chart",
                })
              : intl.formatMessage({ id: "Overview: empty chart", defaultMessage: "No data yet" })}
        </Text>
      ) : null}
    </>
  );
};
